import slugify from "slugify";
import { DateTime } from "luxon";

import { NULLABLE_VALUES, ONE_BILLION, ONE_MILLION } from "../constants";
import sdg_1 from "../assets/images/sdg_icons/E-WEB-Goal-01.png";
import sdg_2 from "../assets/images/sdg_icons/E-WEB-Goal-02.png";
import sdg_3 from "../assets/images/sdg_icons/E-WEB-Goal-03.png";
import sdg_4 from "../assets/images/sdg_icons/E-WEB-Goal-04.png";
import sdg_5 from "../assets/images/sdg_icons/E-WEB-Goal-05.png";
import sdg_6 from "../assets/images/sdg_icons/E-WEB-Goal-06.png";
import sdg_7 from "../assets/images/sdg_icons/E-WEB-Goal-07.png";
import sdg_8 from "../assets/images/sdg_icons/E-WEB-Goal-08.png";
import sdg_9 from "../assets/images/sdg_icons/E-WEB-Goal-09.png";
import sdg_10 from "../assets/images/sdg_icons/E-WEB-Goal-10.png";
import sdg_11 from "../assets/images/sdg_icons/E-WEB-Goal-11.png";
import sdg_12 from "../assets/images/sdg_icons/E-WEB-Goal-12.png";
import sdg_13 from "../assets/images/sdg_icons/E-WEB-Goal-13.png";
import sdg_14 from "../assets/images/sdg_icons/E-WEB-Goal-14.png";
import sdg_15 from "../assets/images/sdg_icons/E-WEB-Goal-15.png";
import sdg_16 from "../assets/images/sdg_icons/E-WEB-Goal-16.png";
import sdg_17 from "../assets/images/sdg_icons/E-WEB-Goal-17.png";
import { MESSAGES } from "../messages";

const sdgIcons: { [key: number]: string } = {
  1: sdg_1,
  2: sdg_2,
  3: sdg_3,
  4: sdg_4,
  5: sdg_5,
  6: sdg_6,
  7: sdg_7,
  8: sdg_8,
  9: sdg_9,
  10: sdg_10,
  11: sdg_11,
  12: sdg_12,
  13: sdg_13,
  14: sdg_14,
  15: sdg_15,
  16: sdg_16,
  17: sdg_17,
};

export const getSdgIcon = (goalNumber: number): string => {
  return sdgIcons[goalNumber] || "";
};

export const formatReadable = (inputString: string): string => {
  if (!inputString) return "";
  return inputString
    .split(" ")
    .map(
      (word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`,
    )
    .join(" ")
    .replace("_", " ")
    .replace(/_/g, " ");
};

export const formatPct = (num: number, keepZero = false): string => {
  try {
    const percentage = num * 100;
    const roundedPercentage = Math.floor(percentage * 100) / 100;
    const roundedPercentageString = `${roundedPercentage.toFixed(2)}%`;
    if (keepZero) {
      return roundedPercentageString;
    }
    return roundedPercentageString === "0.00%"
      ? "<0.01%"
      : roundedPercentageString;
  } catch {
    return num.toString();
  }
};

export const formatNumber = (num: number, unit?: "M" | "B"): string => {
  let formattedNum = num;
  if (unit) {
    const unitValue = unit === "M" ? ONE_MILLION : ONE_BILLION;
    formattedNum = num / unitValue;
  }
  const numString = formattedNum.toLocaleString("en-US", {
    minimumFractionDigits: formattedNum % 1 === 0 ? 0 : 2,
    maximumFractionDigits: formattedNum % 1 === 0 ? 0 : 2,
  });
  return numString + (unit ? unit : "");
};

export const formatReadableDate = (isoDateString: string): string => {
  try {
    const date = new Date(isoDateString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };

    const readableDate = new Intl.DateTimeFormat("en-US", options).format(date);
    return readableDate;
  } catch (error) {
    console.error(error);
    return isoDateString;
  }
};

export const formatDateYMD = (s: string | null): string =>
  !s || s === MESSAGES.NOT_AVAILABLE
    ? MESSAGES.NOT_AVAILABLE_TEXT
    : s.slice(0, 10);

export const formatDateShort = (isoString: string | null): string | null => {
  if (!isoString || isoString === MESSAGES.NOT_AVAILABLE) {
    return MESSAGES.NOT_AVAILABLE_TEXT;
  }
  try {
    const date = new Date(isoString);
    const dateString = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    if (dateString === "Invalid Date") {
      return MESSAGES.NOT_AVAILABLE_TEXT;
    }
    return dateString;
  } catch (error) {
    console.error(error);
    return isoString;
  }
};

export const excelDateToJSDate = (excelDate: number): Date =>
  new Date((excelDate - 25569) * 86400 * 1000);

export const toSlug = (inputString: string): string => {
  return slugify(inputString, { lower: true });
};

export const formatRelativeTime = (isoString: string): string => {
  const timestamp = new Date(isoString);
  const now = new Date();
  const diff = now.getTime() - timestamp.getTime(); // Difference in milliseconds

  // Time differences in milliseconds
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  const month = 30 * day; // Approximate
  const year = 365 * day; // Approximate

  let key: string;

  // Determine the range key
  if (diff < 3 * minute) {
    key = "justNow";
  } else if (diff < hour) {
    key = "minutes";
  } else if (diff < day) {
    key = "hours";
  } else if (diff < week) {
    key = "days";
  } else if (diff < month) {
    key = "weeks";
  } else if (diff < year) {
    key = "months";
  } else {
    key = "years";
  }

  // Handle each case with a switch
  switch (key) {
    case "justNow":
      return "Updated just now";
    case "minutes":
      const minutes = Math.floor(diff / minute);
      return `Updated ${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    case "hours":
      const hours = Math.floor(diff / hour);
      return `Updated ${hours} hour${hours > 1 ? "s" : ""} ago`;
    case "days":
      const days = Math.floor(diff / day);
      return `Updated ${days} day${days > 1 ? "s" : ""} ago`;
    case "weeks":
      const weeks = Math.floor(diff / week);
      return `Updated ${weeks} week${weeks > 1 ? "s" : ""} ago`;
    case "months":
      const months = Math.floor(diff / month);
      return `Updated ${months} month${months > 1 ? "s" : ""} ago`;
    case "years":
      const years = Math.floor(diff / year);
      return `Last updated ${years} year${years > 1 ? "s" : ""} ago`;
    default:
      return "Unknown time";
  }
};

export const cutOffString = (input: string, substring: string): string => {
  const index = input.indexOf(substring);
  return index !== -1 ? input.slice(0, index) : input;
};

export const isNotNan = (
  value: string | null | number | undefined,
): boolean => {
  const isStringOrNumber =
    value !== null && value !== undefined && value !== "";

  if (typeof value === "string") {
    return isStringOrNumber && !NULLABLE_VALUES.includes(value);
  }

  return isStringOrNumber;
};

export const getQuarterAndYearFromExcelDate = (
  excelDate: number,
  offsetQuarters = 0,
): string => {
  const date = DateTime.fromJSDate(excelDateToJSDate(excelDate)); // Convert JS Date to Luxon DateTime
  let quarter = date.quarter + offsetQuarters;
  let year = date.year;
  // Adjust quarter and year correctly when moving forward or backward
  year +=
    quarter > 4
      ? Math.floor((quarter - 1) / 4)
      : quarter < 1
        ? Math.ceil((quarter - 1) / 4)
        : 0;
  quarter = ((quarter - 1 + 4) % 4) + 1; // Ensures quarter stays within 1-4 range

  return `Q${quarter} ${year}`;
};

export const sortQuarters = <T extends { quarter: string }>(
  quarters: T[],
): T[] => {
  return quarters.sort((a, b) => {
    const [qA, yearA] = a.quarter.split(" ");
    const [qB, yearB] = b.quarter.split(" ");

    const quarterA = parseInt(qA.replace("Q", ""), 10);
    const quarterB = parseInt(qB.replace("Q", ""), 10);

    const yearNumA = parseInt(yearA, 10);
    const yearNumB = parseInt(yearB, 10);

    return yearNumA - yearNumB || quarterA - quarterB;
  });
};

export const validateLogo = (
  url: string,
  {
    minDimension = 30,
    minAspectRatio = 0.5,
    maxAspectRatio = 2,
    fallbackUrl = "",
  }: {
    minDimension?: number;
    minAspectRatio?: number;
    maxAspectRatio?: number;
    fallbackUrl?: string;
  } = {},
): Promise<{ isValid: boolean; url: string }> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = (): void => {
      const { naturalWidth, naturalHeight } = img;
      const aspectRatio = naturalWidth / naturalHeight;

      const isValidSize =
        naturalWidth >= minDimension && naturalHeight >= minDimension;
      const isValidAspectRatio =
        aspectRatio >= minAspectRatio && aspectRatio <= maxAspectRatio;

      if (isValidSize && isValidAspectRatio) {
        resolve({ isValid: true, url });
      } else {
        resolve({ isValid: false, url: fallbackUrl });
      }
    };

    img.onerror = (): void => {
      resolve({ isValid: false, url: fallbackUrl });
    };

    img.src = url;
  });
};

export const renderTextSample = (text: string, length = 100): string => {
  if (text.length <= length) {
    return text;
  }
  return text.substring(0, length) + "...";
};
